/**
 * A ticket's files (TMMAPP-0142 U2 item 25d): each a chip that downloads it through
 * `GET /v1/me/tickets/{number}/attachments/{id}` (bearer only, so a Blob, never a plain link).
 * Nothing is kept in the browser.
 */
import { useState } from 'react';
import { Box, ButtonBase, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ticketsApi, type TicketAttachment } from '@/api/tickets';
import { DownloadIcon } from '@/components/icons';
import { formatByteSize } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';
import { downloadBlob } from '@/utils/downloadBlob';
import { AttachmentKindIcon } from '../composer/AttachmentFacts';

function kindOf(kind: string): 'IMAGE' | 'PDF' | 'SHEET' | null {
  return kind === 'IMAGE' || kind === 'PDF' || kind === 'SHEET' ? kind : null;
}

function TicketFile({ number, file }: { number: number; file: TicketAttachment }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  async function save() {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const { blob, filename } = await ticketsApi.downloadAttachment(number, file.attachmentId);
      downloadBlob(blob, filename ?? file.name);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack sx={{ gap: 0.25, minWidth: 0 }}>
      <ButtonBase
        onClick={() => void save()}
        aria-label={t('support.tickets.download', { name: file.name })}
        aria-busy={busy}
        data-ticket-file={file.attachmentId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 1,
          p: 0.75,
          paddingInlineEnd: 1.25,
          maxWidth: '100%',
          minWidth: 0,
          textAlign: 'start',
          border: `1px solid ${tokens.line}`,
          borderRadius: 1.5,
          bgcolor: tokens.surface,
          color: tokens.text,
          '&:hover': { bgcolor: tokens.hover },
        }}
      >
        <Box sx={{ display: 'flex', color: tokens.text2 }}>
          <AttachmentKindIcon kind={kindOf(file.kind)} />
        </Box>
        <Stack sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            component="span"
            sx={{
              fontSize: 13,
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {file.name}
          </Typography>
          <Typography component="span" sx={{ fontSize: 12, color: tokens.text2 }}>
            {formatByteSize(file.sizeBytes, locale)}
          </Typography>
        </Stack>
        <Box sx={{ color: tokens.text2, display: 'flex' }}>
          <DownloadIcon />
        </Box>
      </ButtonBase>
      {failed && (
        <Typography sx={{ fontSize: 12, color: tokens.errorText }} data-ticket-file-failed="">
          {t('support.tickets.downloadFailed', { name: file.name })}
        </Typography>
      )}
    </Stack>
  );
}

export function TicketAttachments({
  number,
  files,
}: {
  number: number;
  files: readonly TicketAttachment[];
}) {
  const { t } = useTranslation();
  if (files.length === 0) return null;
  return (
    <Stack
      role="list"
      aria-label={t('support.tickets.files')}
      sx={{ gap: 0.75, mt: 0.75 }}
      data-ticket-files=""
    >
      {files.map((file) => (
        <Box role="listitem" key={file.attachmentId} sx={{ minWidth: 0 }}>
          <TicketFile number={number} file={file} />
        </Box>
      ))}
    </Stack>
  );
}
